import { customElement, FASTElement, html, observable } from '@microsoft/fast-element';
import { GeneralTabTemplate as template } from './general-tab.template';
import { GeneralTabStyles as styles } from './general-tab.styles';
import {
  NumberField,
  SegmentedControl,
  Select,
  TextArea,
  TextField,
} from '@genesislcap/foundation-zero';
import { CASCADE_ENUM, EnumTranslate, IEnumService } from '../../../../../services/enum.service';
import { IProjectModalService, IProjectService } from '../../../../../services';
import {
  clearErrorUtil,
  displayErrorUtil,
  PROJECT_OPERATIONAL_STATUS,
  TECHNOLOGY,
} from '../../../../../utils';
import { Project } from '../../../../../types';
import { ICompanyService } from '../../../../../services/company.service';
import { logger, Message } from '@genesislcap/foundation-comms';
import { updateArray } from '../../../../../utils/dataserver';
import { roundNumber } from '../../../../../utils/formatting';

const name = 'general-tab';

export interface IProjectOperationalStatus {
  projectOperationalStatus: PROJECT_OPERATIONAL_STATUS;
  shouldConfirm: boolean;
}

@customElement({
  name,
  template,
  styles,
})
export class GeneralTab extends FASTElement {
  @IProjectModalService projectModalService: IProjectModalService;
  @IProjectService projectService: IProjectService;
  @IEnumService enumService: IEnumService;
  @ICompanyService companyService: ICompanyService;

  // General Tab Elements
  @observable projectName: TextField;
  @observable legalName: Select;
  @observable projectOperationalStatus: SegmentedControl;
  @observable owner: Select;
  @observable operator: TextField;
  @observable capacityFactor: TextField;
  @observable techType: SegmentedControl;
  // Wind Fields
  @observable windCapacityMWAC: TextField;
  @observable windAvailableCapacityMWAC: TextField;
  @observable turbineMakeModel: TextField;
  @observable turbineCount: TextField;
  @observable hubHeight: NumberField;
  @observable turbineNameplate: TextField;
  @observable turbineDiameter: NumberField;
  // Solar Fields
  @observable solarCapacityMWDC: TextField;
  @observable solarCapacityMWAC: TextField;
  @observable solarAvailableCapacityMWAC: TextField;
  @observable panelType: Select;
  @observable panelTechnology: TextField;
  @observable trackingType: TextField;
  @observable panelMakeModel: TextField;

  // General Tab Form Values
  @observable generalForm: HTMLFormElement;
  @observable projectNameValue: string = '';
  @observable legalNameValue: string = '';
  @observable projectOperationalStatusValue: PROJECT_OPERATIONAL_STATUS =
    PROJECT_OPERATIONAL_STATUS.OPERATIONAL;
  @observable ownerValue: string = '';
  @observable operatorValue: string = '';
  @observable capacityFactorValue: string = '';
  @observable techTypeValue: 'WIND' | 'SOLAR' = 'WIND';
  // Wind Field Values
  @observable windCapacityMWACValue: string = '';
  @observable availableCapacityMWACValue: string = '';
  @observable turbineMakeModelValue: string = '';
  @observable turbineCountValue: string = '';
  @observable hubHeightValue: string = '';
  @observable turbineNameplateValue: string = '';
  @observable turbineDiameterValue: string = '';
  // Solar Field Values
  @observable solarCapacityMWDCValue: string = '';
  @observable solarCapacityMWACValue: string = '';
  @observable solarAvailableCapacityMWACValue: string = '';
  @observable panelTypeValue: string = '';
  @observable panelTechnologyValue: string = '';
  @observable panelMakeModelValue: string = '';
  @observable trackingTypeValue: string = '';
  // ENUMs
  @observable panelTechnologyEnum: Array<EnumTranslate>;
  @observable panelTypeEnum: Array<EnumTranslate>;
  @observable trackingTypeEnum: Array<EnumTranslate>;

  @observable tempProject: Project = null;
  @observable isOperational: boolean = false;

  private existingProjectError = 'Project Name already exists';
  private existingLegalProjectError = 'Project Legal Name already exists';

  public _requiredFormFields = [];
  @observable public companies: Array<any> = [];

  connectedCallback() {
    super.connectedCallback();

    this._requiredFormFields = [
      this.projectName,
      this.operator,
      this.windCapacityMWAC,
      this.windAvailableCapacityMWAC,
      this.turbineMakeModel,
      this.turbineCount,
      this.hubHeight,
      this.turbineNameplate,
      this.turbineDiameter,
    ];

    this.loadEnums();
    this.addRequiredFieldsToService();
    this.attachedBlurEvent();
    this.loadCompanyNames();
  }

  private async loadEnums() {
    this.enumService.getEnumValues(CASCADE_ENUM.PANEL_TECHNOLOGY).then((response) => {
      if (response.REPLY) {
        this.panelTechnologyEnum = response.REPLY;
      }
    });

    this.enumService.getEnumValues(CASCADE_ENUM.PANEL_TYPE).then((response) => {
      if (response.REPLY) {
        this.panelTypeEnum = response.REPLY;
      }
    });

    this.enumService.getEnumValues(CASCADE_ENUM.TRACKING_TYPE).then((response) => {
      if (response.REPLY) {
        this.trackingTypeEnum = response.REPLY;
      }
    });
  }

  private attachedBlurEvent() {
    this._requiredFormFields.forEach((field) => {
      field.addEventListener('blur', () => this.checkFieldValidity(field));
    });
  }

  public checkFormValidity() {
    this._requiredFormFields.forEach((field) => {
      this.checkFieldValidity(field);
    });
  }

  public blurRoundNumber(field, value, fieldName) {
    if (roundNumber(value)) {
      this[fieldName] = roundNumber(value).toString();
    } else {
      this[fieldName] = null;
      setTimeout(() => {
        this.checkFieldValidity(field);
      }, 100);
    }
  }

  private checkFieldValidity(elm: TextField | TextArea | Select) {
    if (!elm.value) {
      displayErrorUtil(elm);
      this.amendValidList(elm, false);
    } else {
      if (elm === this.projectName) {
        if (this.projectModalService.modalMode === 'create') {
          this.nameExists(false);
        } else {
          if (this.tempProject.NAME !== elm.value) {
            this.nameExists(false);
          } else {
            clearErrorUtil(elm);
            this.amendValidList(elm, true);
          }
        }
      } else if (elm === this.solarCapacityMWDC || elm == this.solarCapacityMWAC) {
        this.checkSolarCapacity();
      } else {
        clearErrorUtil(elm);
        this.amendValidList(elm, true);
      }
    }
  }

  private nameExists(isLegalName: boolean) {
    this.projectModalService
      .getProjectNames(isLegalName ? this.legalNameValue : this.projectNameValue, isLegalName)
      .then((response) => {
        if (response && response.ROW) {
          const errorMessage = isLegalName
            ? this.existingLegalProjectError
            : this.existingProjectError;
          displayErrorUtil(isLegalName ? this.legalName : this.projectName, errorMessage);
          this.amendValidList(isLegalName ? this.legalName : this.projectName, false);
        } else {
          clearErrorUtil(isLegalName ? this.legalName : this.projectName);
          this.amendValidList(isLegalName ? this.legalName : this.projectName, true);
        }
      });
  }

  public technologyChanged(isWind: boolean) {
    this.resetWindSolarFields();

    setTimeout(() => {
      if (isWind) {
        this._requiredFormFields = [
          this.projectName,
          this.operator,
          this.windCapacityMWAC,
          this.windAvailableCapacityMWAC,
          this.turbineMakeModel,
          this.turbineCount,
          this.hubHeight,
          this.turbineNameplate,
          this.turbineDiameter,
        ];
        this.projectModalService.addToRequiredFormField([
          this.windCapacityMWAC,
          this.windAvailableCapacityMWAC,
          this.turbineMakeModel,
          this.turbineCount,
          this.hubHeight,
          this.turbineNameplate,
          this.turbineDiameter,
        ]);
        this.projectModalService.removeFromRequiredFormField([
          this.solarCapacityMWDC,
          this.solarCapacityMWAC,
          this.solarAvailableCapacityMWAC,
          this.panelMakeModel,
        ]);
      } else {
        this._requiredFormFields = [
          this.projectName,
          this.operator,
          this.solarCapacityMWDC,
          this.solarCapacityMWAC,
          this.solarAvailableCapacityMWAC,
          this.panelMakeModel,
        ];
        this.projectModalService.removeFromRequiredFormField([
          this.windCapacityMWAC,
          this.windAvailableCapacityMWAC,
          this.turbineMakeModel,
          this.turbineCount,
          this.hubHeight,
          this.turbineNameplate,
          this.turbineDiameter,
        ]);
        this.projectModalService.addToRequiredFormField([
          this.solarCapacityMWDC,
          this.solarCapacityMWAC,
          this.solarAvailableCapacityMWAC,
          this.panelMakeModel,
        ]);
      }
      this.attachedBlurEvent();
    }, 500);
  }

  public panelTypeChanged(isBifacial: boolean) {
    if (isBifacial) {
      this.panelTechnologyValue = 'CSI_MONOCRYSTALLINE_SILICON';
    }
  }

  private resetWindSolarFields() {
    const windSolarFields = [
      this.windCapacityMWAC,
      this.windAvailableCapacityMWAC,
      this.turbineMakeModel,
      this.turbineCount,
      this.hubHeight,
      this.turbineNameplate,
      this.turbineDiameter,
      this.solarCapacityMWDC,
      this.solarCapacityMWAC,
      this.solarAvailableCapacityMWAC,
      this.panelMakeModel,
    ];

    this.windCapacityMWACValue = null;
    this.availableCapacityMWACValue = null;
    this.turbineMakeModelValue = '';
    this.turbineCountValue = '';
    this.hubHeightValue = null;
    this.turbineNameplateValue = null;
    this.turbineDiameterValue = null;
    this.solarCapacityMWDCValue = null;
    this.solarCapacityMWACValue = null;
    this.solarAvailableCapacityMWACValue = null;
    this.panelTypeValue = 'MONO';
    this.panelTechnologyValue = 'CSI_MONOCRYSTALLINE_SILICON';
    this.panelMakeModelValue = '';
    this.trackingTypeValue = 'FIXED_TRACKER';

    windSolarFields.forEach((elm) => {
      this.amendValidList(elm, false);
      if (elm) {
        const error = elm.getElementsByClassName('errorMessage');
        if (error.length) {
          error[0].remove();
          elm.classList.remove('withError');
        }
      }
    });
  }

  /**
   * Adds and removed fields from the completed fields list and Emits value to parent component
   * @param field
   * @param isValid
   */
  private amendValidList(field: TextField | TextArea | Select | NumberField, isValid: boolean) {
    if (isValid) {
      this.projectModalService.addToTotalCompletedFields(field);
    } else {
      this.projectModalService.removeFromTotalCompletedFields(field);
    }

    this.checkTabCompletionStatus();
  }

  private checkTabCompletionStatus() {
    const tabCompleted = this._requiredFormFields.every((elm) =>
      this.projectModalService.totalCompletedFields.includes(elm),
    );
    this.$emit('generalTabCompleted', tabCompleted);
  }

  public clearForm() {
    const allFields = [
      this.projectName,
      this.legalName,
      this.owner,
      this.operator,
      this.windCapacityMWAC,
      this.windAvailableCapacityMWAC,
      this.turbineMakeModel,
      this.turbineCount,
      this.hubHeight,
      this.turbineNameplate,
      this.turbineDiameter,
      this.solarCapacityMWDC,
      this.solarCapacityMWAC,
      this.solarAvailableCapacityMWAC,
      this.panelType,
      this.panelTechnology,
      this.panelMakeModel,
      this.trackingType,
      this.capacityFactor,
    ];

    allFields.forEach((field) => {
      if (field) {
        clearErrorUtil(field);
      }
    });

    this.projectNameValue = '';
    this.legalNameValue = this.companyService.getCurrentCompany().NAME;
    this.ownerValue = this.companyService.getCurrentCompany().NAME;
    this.operatorValue = '';
    this.windCapacityMWACValue = null;
    this.availableCapacityMWACValue = null;
    this.turbineMakeModelValue = '';
    this.turbineCountValue = '';
    this.hubHeightValue = null;
    this.turbineNameplateValue = null;
    this.turbineDiameterValue = null;
    this.solarCapacityMWDCValue = null;
    this.solarCapacityMWACValue = null;
    this.solarAvailableCapacityMWACValue = null;
    this.panelTypeValue = 'MONO';
    this.panelTechnologyValue = 'CSI_MONOCRYSTALLINE_SILICON';
    this.panelMakeModelValue = ' ';
    this.trackingTypeValue = 'FIXED_TRACKER';
    this.capacityFactorValue = null;

    this.isOperational = false;
    this.tempProject = null;

    // Reset the selection for the Technology Type (Segmented Control)
    this.techTypeValue = 'WIND';
    this.technologyChanged(true);
    this.projectOperationalStatusValue = PROJECT_OPERATIONAL_STATUS.OPERATIONAL;

    // Inform the Project Modal that general tab isn't completed
    this.$emit('generalTabCompleted', false);
  }

  public populateForm(project: Project) {
    this.tempProject = project;
    this.addRequiredFieldsToService();

    if (project.NAME) {
      this.projectNameValue = project.NAME;
      this.amendValidList(this.projectName, true);
      if (this.projectModalService.modalMode === 'create') {
        this.nameExists(false);
      }
    }

    if (project.LEGAL_NAME) {
      this.legalNameValue = project.LEGAL_NAME;
    }

    this.projectOperationalStatusValue =
      PROJECT_OPERATIONAL_STATUS[project.PROJECT_OPERATIONAL_STATUS];

    if (this.projectOperationalStatusValue === PROJECT_OPERATIONAL_STATUS.OPERATIONAL) {
      this.isOperational = true;
    }

    if (project.OWNER) {
      this.ownerValue = project.OWNER;
    }

    if (project.PROJECT_OPERATOR) {
      this.operatorValue = project.PROJECT_OPERATOR;
      this.amendValidList(this.operator, true);
    }

    this.techTypeValue = project.TECHNOLOGY;
    this.capacityFactorValue = project.P50_CAPACITY_FACTOR
      ? String(project.P50_CAPACITY_FACTOR)
      : '';

    if (project.TECHNOLOGY === TECHNOLOGY.WIND) {
      this.technologyChanged(true);

      setTimeout(() => {
        if (project.MEGAWATT_AC) {
          this.windCapacityMWACValue = project.MEGAWATT_AC ? String(project.MEGAWATT_AC) : '';
          this.amendValidList(this.windCapacityMWAC, true);
        }

        if (project.AVAILABLE_CAPACITY_AC) {
          this.availableCapacityMWACValue = project.AVAILABLE_CAPACITY_AC
            ? String(project.AVAILABLE_CAPACITY_AC)
            : '';
          this.amendValidList(this.windAvailableCapacityMWAC, true);
        }

        if (project.TURBINE_MAKE_AND_MODEL) {
          this.turbineMakeModelValue = project.TURBINE_MAKE_AND_MODEL;
          this.amendValidList(this.turbineMakeModel, true);
        }

        if (project.TURBINE_COUNT) {
          this.turbineCountValue = project.TURBINE_COUNT ? String(project.TURBINE_COUNT) : '';
          this.amendValidList(this.turbineCount, true);
        }

        if (project.HUB_HEIGHT) {
          this.hubHeightValue = project.HUB_HEIGHT ? String(project.HUB_HEIGHT) : '';
          this.amendValidList(this.hubHeight, true);
        }

        if (project.TURBINE_MEGAWATT) {
          this.turbineNameplateValue = project.TURBINE_MEGAWATT
            ? String(project.TURBINE_MEGAWATT)
            : '';
          this.amendValidList(this.turbineNameplate, true);
        }

        if (project.TURBINE_DIAMETER) {
          this.turbineDiameterValue = project.TURBINE_DIAMETER
            ? String(project.TURBINE_DIAMETER)
            : '';
          this.amendValidList(this.turbineDiameter, true);
        }
      }, 500);
    } else if (project.TECHNOLOGY === TECHNOLOGY.SOLAR) {
      this.technologyChanged(false);

      setTimeout(() => {
        if (project.MEGAWATT_DC) {
          this.solarCapacityMWDCValue = project.MEGAWATT_DC ? String(project.MEGAWATT_DC) : '';
          this.checkSolarCapacity();
        }

        if (project.MEGAWATT_AC) {
          this.solarCapacityMWACValue = project.MEGAWATT_AC ? String(project.MEGAWATT_AC) : '';
          this.amendValidList(this.solarCapacityMWAC, true);
          this.checkSolarCapacity();
        }

        if (project.AVAILABLE_CAPACITY_AC) {
          this.solarAvailableCapacityMWACValue = project.AVAILABLE_CAPACITY_AC
            ? String(project.AVAILABLE_CAPACITY_AC)
            : '';
          this.amendValidList(this.solarAvailableCapacityMWAC, true);
        }

        if (project.PANEL_MAKE_AND_MODEL) {
          this.panelMakeModelValue = project.PANEL_MAKE_AND_MODEL;
          this.amendValidList(this.panelMakeModel, true);
        }

        if (this.projectModalService.modalMode === 'edit') {
          this.panelTypeValue = project.PANEL_TYPE;
          this.panelTechnologyValue = project.PANEL_TECHNOLOGY;
          this.trackingTypeValue = project.TRACKING_TYPE;
        } else {
          // If you're populating these fields for Draft Project
          this.panelTypeValue = project.PANEL_TYPE_STRING;
          this.panelTechnologyValue = project.PANEL_TECHNOLOGY_STRING;
          this.trackingTypeValue = project.TRACKING_TYPE_STRING;
        }
      }, 500);
    }
  }

  public addRequiredFieldsToService() {
    // Update service with latest required fields
    this.projectModalService.addToRequiredFormField(this._requiredFormFields);
  }

  public setProjectOperationalStatus(projectOperationalStatus: PROJECT_OPERATIONAL_STATUS) {
    this.projectOperationalStatusValue = PROJECT_OPERATIONAL_STATUS[projectOperationalStatus];
  }

  public projectOperationalStatusValueChanged(oldValue, newValue: PROJECT_OPERATIONAL_STATUS) {
    let shouldConfirm = false;

    if (
      !this.isOperational &&
      newValue === PROJECT_OPERATIONAL_STATUS.OPERATIONAL &&
      this.tempProject !== null
    ) {
      shouldConfirm = true;
    }

    const projectOperationalStatusCallbackObject: IProjectOperationalStatus = {
      projectOperationalStatus: newValue,
      shouldConfirm: shouldConfirm,
    };

    this.$emit('projectOperationalStatusChanged', projectOperationalStatusCallbackObject);
  }

  private checkSolarCapacity() {
    let errorMessage =
      'Nameplate Capacity Megawatt (DC) value must be greater than the Nameplate Capacity Megawatt (AC) value';
    if (
      (parseInt(this.solarCapacityMWDCValue) || 0) <= (parseInt(this.solarCapacityMWACValue) || 0)
    ) {
      displayErrorUtil(this.solarCapacityMWDC, errorMessage);
      this.amendValidList(this.solarCapacityMWDC, false);
    } else {
      this.amendValidList(this.solarCapacityMWDC, true);
      this.amendValidList(this.solarCapacityMWAC, true);
      clearErrorUtil(this.solarCapacityMWDC);
    }
  }

  public async loadCompanyNames() {
    const currentCompanyObject = {
      NAME: this.companyService.getCurrentCompany().NAME,
    };

    await this.companyService
      .getMyCompanies(
        `CASCADE_PARENT_COMPANY_ID == '${
          this.companyService.getCurrentCompany().CASCADE_COMPANY_ID
        }'`,
      )
      .subscribe({
        next: (streamMessage: Message) => {
          const companiesResultList = streamMessage.ROW ?? null;

          if (companiesResultList) {
            this.companies = updateArray(companiesResultList, this.companies);
          }

          if (!this.companies.includes(currentCompanyObject)) {
            this.companies.unshift(currentCompanyObject);
          } else {
            // Already added, order to first position
            this.companies.sort((x) => (x === currentCompanyObject ? -1 : 0));
          }
        },
        error: (error: any) => {
          logger.error(error);
        },
      });

    this.legalNameValue = this.companyService.getCurrentCompany().NAME;
    this.ownerValue = this.companyService.getCurrentCompany().NAME;
  }

  public getNameplateLabel(technology: string, current?: string) {
    if (technology === TECHNOLOGY.SOLAR && current === 'DC') {
      return this.projectOperationalStatusValue === PROJECT_OPERATIONAL_STATUS.OPERATIONAL
        ? html`
            Nameplate Capacity (MW
            <sub>DC</sub>
            )
          `
        : html`
            Expected Nameplate Capacity (MW
            <sub>DC</sub>
            )
          `;
    } else if (technology === TECHNOLOGY.WIND || (technology === TECHNOLOGY.SOLAR && current === 'AC')) {
      return this.projectOperationalStatusValue === PROJECT_OPERATIONAL_STATUS.OPERATIONAL
        ? html`
            Nameplate Capacity (MW
            <sub>AC</sub>
            )
          `
        : html`
            Expected Nameplate Capacity (MW
            <sub>AC</sub>
            )
          `;
    }
  }
}
